import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useUser, useClerk, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useAuthStore } from '../stores/authStore';
import { api } from '../api/client.js';
import { isConnectionLost, watchConnection } from '../api/connection';
import { trackSignupConversion } from '../utils/analytics';
import { isPrerendering } from '../utils/prerender';
import { forgetUser, identifyUser, track } from '../analytics/track';
import { expectSignOut, useSteadySignedIn } from './steadySignedIn';
import { clearIdeaDrafts } from '../utils/ideaDraft';

export const AuthContext = createContext(null);

// How long to wait before each retry of /auth/me. Right after a sign-in Clerk's
// session token can lag a beat behind isSignedIn (a 401), and the API can blip
// during a deploy: neither means the person is signed out.
export const AUTH_RETRY_DELAYS_MS = [400, 1200, 2500];

const retryable = (error) => {
  const status = error?.response?.status;
  // No response at all (network, CORS during a deploy), the token race, or the server
  return !status || status === 401 || status === 408 || status === 429 || status >= 500;
};

// Once those are spent the page says it could not load the account, and keeps
// asking on its own: the API was away (a restart, a lost connection) and comes
// back without anyone pressing "Try again". The last delay repeats.
export const AUTH_RECOVERY_DELAYS_MS = [3000, 6000, 12000, 20000, 30000];

export async function fetchUserWithRetry(fetchUser, delays = AUTH_RETRY_DELAYS_MS, isCancelled = () => false) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fetchUser();
    } catch (error) {
      if (attempt >= delays.length || !retryable(error) || isCancelled()) throw error;
      await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
      if (isCancelled()) throw error;
    }
  }
}

export const AuthProvider = ({ children }) => {
  const store = useAuthStore();
  const { user: clerkUser, isLoaded: clerkLoaded } = useUser();
  const { isLoaded: authLoaded, isSignedIn: clerkSignedIn } = useClerkAuth();
  const { signOut } = useClerk();
  // Bumped by "Try again" after the API would not give us the user
  const [attempt, setAttempt] = useState(0);
  // Clerk hands out a new user object whenever the session refreshes; only a new
  // person (or a sign-in/out) is a reason to fetch the app user again
  // Clerk's own answer can flicker to signed out for a moment mid-session; the
  // app only believes it once it has lasted (steadySignedIn.js)
  const steady = useSteadySignedIn(clerkLoaded && authLoaded, clerkSignedIn, clerkUser?.id ?? null);
  const isSignedIn = steady.signedIn;
  const clerkUserId = steady.userId;

  useEffect(() => {
    // Skip auth sync during prerendering (react-snap)
    if (isPrerendering()) {
      store.setLoading(false);
      return undefined;
    }

    if (!clerkLoaded || !authLoaded) {
      store.setLoading(true);
      return undefined;
    }

    if (!isSignedIn || !clerkUserId) {
      store.clearAuth();
      return undefined;
    }

    const current = useAuthStore.getState();
    if (current.isAuthenticated && current.clerkUserId === clerkUserId) {
      return undefined;
    }

    // Signed in to Clerk, app user not fetched yet: this is "loading", never
    // "signed out". Treating the gap as signed out is what sent every fresh
    // sign-in from the page it asked for to /login and on to the home page.
    let cancelled = false;
    store.startAuth();

    (async () => {
      try {
        const backendUser = await fetchUserWithRetry(api.getCurrentUser, AUTH_RETRY_DELAYS_MS, () => cancelled);
        if (cancelled) return;
        store.completeAuth({ user: backendUser, clerkUserId });
        identifyUser(backendUser);

        // Google Ads sign-up: once, and only for an account made in the last day
        trackSignupConversion(backendUser);
      } catch (e) {
        if (cancelled) return;
        console.error('AuthProvider: backend user fetch failed', e);
        const reason = e?.response?.status ? `http_${e.response.status}` : 'network';
        store.failAuth(reason);
        track('auth_account_error', { reason });
      }
    })();

    return () => { cancelled = true; };
  }, [clerkLoaded, authLoaded, isSignedIn, clerkUserId, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  // The account could not be fetched: ask again on a backoff, at once when any
  // other request gets through again, when the browser is back online and when
  // the tab is looked at again. The error card stays until one of them works.
  const authError = store.authError;
  useEffect(() => {
    if (!authError || !isSignedIn || !clerkUserId) return undefined;
    let cancelled = false;
    let timer = null;
    let tries = 0;
    let asking = false;

    const schedule = () => {
      clearTimeout(timer);
      const delay = AUTH_RECOVERY_DELAYS_MS[Math.min(tries, AUTH_RECOVERY_DELAYS_MS.length - 1)];
      tries += 1;
      timer = setTimeout(askAgain, delay);
    };
    const askAgain = async () => {
      if (cancelled || asking) return;
      asking = true;
      clearTimeout(timer);
      try {
        const backendUser = await api.getCurrentUser();
        if (cancelled) return;
        store.completeAuth({ user: backendUser, clerkUserId });
        identifyUser(backendUser);
        track('auth_account_recovered', { tries: tries + 1 });
      } catch {
        if (!cancelled) schedule();
      } finally {
        asking = false;
      }
    };

    schedule();
    let wasLost = isConnectionLost();
    const stopWatching = watchConnection((lost) => {
      if (wasLost && !lost) askAgain();
      wasLost = lost;
    });
    const onVisible = () => { if (document.visibilityState === 'visible') askAgain(); };
    window.addEventListener('online', askAgain);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      stopWatching();
      window.removeEventListener('online', askAgain);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [authError, isSignedIn, clerkUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    user: store.user,
    loading: store.loading,
    isAuthenticated: store.isAuthenticated,
    authError: store.authError,
    // Signed in to Clerk, held steady through a momentary flicker
    clerkSignedIn: isSignedIn,
    retryAuth: () => setAttempt((n) => n + 1),
    logout: async () => {
      // Asked for, so believed at once rather than waited out
      expectSignOut();
      try { await signOut(); } finally { store.clearAuth(); forgetUser(); clearIdeaDrafts(); }
    },
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
